import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Copy, Check, Lock, Unlock, ChevronRight,
  Terminal, Code2, Activity, ExternalLink, Hash,
} from 'lucide-react';
import '../styles/api-reference.css';

const API_SECTIONS = [
  {
    group: 'Auth',
    items: [
      {
        id: 'auth-login',
        method: 'POST',
        path: '/api/auth/login',
        title: 'Login',
        auth: 'No',
        description:
          'Autentica un usuario con email y contraseña y devuelve un JWT con alcance al tenant. Usa este token en el header `Authorization: Bearer <token>` para todas las demás peticiones.',
        requestParams: [
          { name: 'email', type: 'string', required: true, description: 'Email del usuario registrado.' },
          { name: 'password', type: 'string', required: true, description: 'Contraseña del usuario.' },
        ],
        responseParams: [
          { name: 'token', type: 'string', required: true, description: 'JWT de acceso firmado.' },
          { name: 'user.id', type: 'string', required: true, description: 'ID único del usuario.' },
          { name: 'user.email', type: 'string', required: true, description: 'Email del usuario.' },
          { name: 'user.role', type: 'admin | editor | viewer', required: true, description: 'Rol dentro del tenant.' },
          { name: 'user.tenantId', type: 'string', required: true, description: 'ID del tenant asociado.' },
        ],
        requestExample: `POST /api/auth/login
Content-Type: application/json

{
  "email": "admin@localhost",
  "password": "admin1234"
}`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: application/json

{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "user": {
    "id": "usr_123",
    "email": "admin@localhost",
    "role": "admin",
    "tenantId": "tenant_default"
  }
}`,
      },
    ],
  },
  {
    group: 'Templates',
    items: [
      {
        id: 'templates-list',
        method: 'GET',
        path: '/api/templates',
        title: 'List templates',
        auth: 'Bearer',
        description:
          'Lista las plantillas disponibles para el usuario dentro del tenant, con soporte de paginación y filtro por carpeta.',
        requestParams: [
          { name: 'page', type: 'number', required: false, description: 'Número de página (default: 1).' },
          { name: 'limit', type: 'number', required: false, description: 'Elementos por página (default: 20, max: 100).' },
          { name: 'folderId', type: 'string', required: false, description: 'Filtra por carpeta específica.' },
        ],
        responseParams: [
          { name: 'items', type: 'Template[]', required: true, description: 'Array de plantillas.' },
          { name: 'total', type: 'number', required: false, description: 'Total de plantillas (si se solicita).' },
        ],
        requestExample: `GET /api/templates?page=1&limit=20
Authorization: Bearer <token>`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: application/json

{
  "items": [
    {
      "id": "tpl_1",
      "name": "Invoice",
      "engine": "handlebars",
      "recipe": "chrome-pdf",
      "folderId": null,
      "created_at": "2026-02-21T17:00:00.000Z"
    }
  ],
  "total": 1
}`,
      },
      {
        id: 'templates-create',
        method: 'POST',
        path: '/api/templates',
        title: 'Create template',
        auth: 'Bearer',
        description:
          'Crea una plantilla nueva sujeta a los límites del plan activo. Soporta HTML con Handlebars y helpers JS personalizados.',
        requestParams: [
          { name: 'name', type: 'string', required: true, description: 'Nombre de la plantilla.' },
          { name: 'engine', type: '"handlebars"', required: true, description: 'Motor de renderizado. Actualmente sólo handlebars.' },
          { name: 'recipe', type: 'chrome-pdf | chrome-image | html | xlsx', required: true, description: 'Tipo de salida.' },
          { name: 'contentHtml', type: 'string', required: true, description: 'HTML de la plantilla con variables Handlebars.' },
          { name: 'helpersJs', type: 'string', required: false, description: 'Código JS con helpers custom de Handlebars.' },
        ],
        responseParams: [
          { name: 'id', type: 'string', required: true, description: 'ID de la plantilla creada.' },
          { name: 'name', type: 'string', required: true, description: 'Nombre asignado.' },
          { name: 'engine', type: 'string', required: true, description: 'Motor utilizado.' },
          { name: 'recipe', type: 'string', required: true, description: 'Receta de salida.' },
          { name: 'created_at', type: 'string (ISO 8601)', required: true, description: 'Timestamp de creación.' },
        ],
        requestExample: `POST /api/templates
Authorization: Bearer <token>
Content-Type: application/json

{
  "name": "Invoice v2",
  "engine": "handlebars",
  "recipe": "chrome-pdf",
  "contentHtml": "<html><body>{{customer.name}}</body></html>",
  "helpersJs": ""
}`,
        responseExample: `HTTP/1.1 201 Created
Content-Type: application/json

{
  "id": "tpl_x31",
  "name": "Invoice v2",
  "engine": "handlebars",
  "recipe": "chrome-pdf",
  "created_at": "2026-02-21T17:00:00.000Z"
}`,
      },
    ],
  },
  {
    group: 'Render',
    items: [
      {
        id: 'render-sync',
        method: 'POST',
        path: '/api/render',
        title: 'Render sync',
        auth: 'Bearer',
        description:
          'Genera un documento de forma síncrona (PDF, imagen o HTML). Aplica límites de plan, watermark condicional y bloqueo si la suscripción está suspendida. La respuesta es el binario del documento.',
        requestParams: [
          { name: 'template.id', type: 'string', required: false, description: 'ID de plantilla guardada. Mutualmente excluyente con contentHtml.' },
          { name: 'template.name', type: 'string', required: false, description: 'Nombre de plantilla guardada.' },
          { name: 'template.contentHtml', type: 'string', required: false, description: 'HTML inline; omite buscar plantilla guardada.' },
          { name: 'template.recipe', type: 'chrome-pdf | chrome-image | html', required: false, description: 'Override de receta.' },
          { name: 'data', type: 'object', required: true, description: 'Datos que se inyectan en la plantilla.' },
          { name: 'options.chrome.*', type: 'object', required: false, description: 'Opciones del navegador headless (format, printBackground, etc.).' },
        ],
        responseParams: [
          { name: '(body)', type: 'binary', required: true, description: 'Contenido del documento generado.' },
          { name: 'X-Report-Id', type: 'header', required: true, description: 'ID del reporte generado.' },
          { name: 'X-Request-Id', type: 'header', required: true, description: 'ID de la petición para trazabilidad.' },
        ],
        requestExample: `POST /api/render
Authorization: Bearer <token>
Content-Type: application/json

{
  "template": {
    "id": "tpl_1",
    "recipe": "chrome-pdf"
  },
  "data": {
    "customer": { "name": "ACME Corp" },
    "invoice": { "number": "INV-001", "total": 4500 }
  },
  "options": {
    "chrome": {
      "format": "A4",
      "printBackground": true
    }
  }
}`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: application/pdf
Content-Disposition: inline; filename="report.pdf"
X-Report-Id: rpt_123
X-Request-Id: req_ab12

%PDF-1.7
...binary content...`,
      },
      {
        id: 'render-preview',
        method: 'POST',
        path: '/api/render/preview',
        title: 'Render preview',
        auth: 'Bearer',
        description:
          'Genera un HTML de vista previa sin persistir el reporte ni consumir cuota del plan. Útil para el editor de plantillas en tiempo real.',
        requestParams: [
          { name: 'template.id', type: 'string', required: false, description: 'ID de plantilla guardada.' },
          { name: 'template.contentHtml', type: 'string', required: false, description: 'HTML inline para preview.' },
          { name: 'data', type: 'object', required: false, description: 'Datos de prueba a inyectar.' },
        ],
        responseParams: [
          { name: '(body)', type: 'text/html', required: true, description: 'HTML renderizado con los datos provistos.' },
        ],
        requestExample: `POST /api/render/preview
Authorization: Bearer <token>
Content-Type: application/json

{
  "template": { "id": "tpl_1" },
  "data": { "customer": { "name": "ACME" } }
}`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: text/html; charset=utf-8

<!DOCTYPE html>
<html>
  <body>
    <h1>ACME</h1>
  </body>
</html>`,
      },
      {
        id: 'render-async',
        method: 'POST',
        path: '/api/render/async',
        title: 'Render async',
        auth: 'Bearer',
        description:
          'Encola el render y devuelve un jobId para consultar el avance. Recomendado para documentos grandes o de alto volumen. Soporta deduplicación por clave.',
        requestParams: [
          { name: 'template.*', type: 'object', required: true, description: 'Igual que /api/render.' },
          { name: 'data', type: 'object', required: true, description: 'Datos a inyectar en la plantilla.' },
          { name: 'options.queue.priority', type: 'number', required: false, description: 'Prioridad del job (1-10, mayor es más urgente).' },
          { name: 'options.queue.dedupeKey', type: 'string', required: false, description: 'Clave de deduplicación; jobs con la misma clave se unifican.' },
        ],
        responseParams: [
          { name: 'jobId', type: 'string', required: true, description: 'ID del job creado.' },
          { name: 'statusUrl', type: 'string', required: true, description: 'URL para consultar estado: GET /api/jobs/:jobId.' },
          { name: 'resultUrl', type: 'string', required: true, description: 'URL para descargar resultado: GET /api/reports/:reportId.' },
        ],
        requestExample: `POST /api/render/async
Authorization: Bearer <token>
Content-Type: application/json

{
  "template": { "id": "tpl_1", "recipe": "chrome-pdf" },
  "data": { "id": "INV-001" },
  "options": {
    "queue": {
      "priority": 5,
      "dedupeKey": "inv-001"
    }
  }
}`,
        responseExample: `HTTP/1.1 202 Accepted
Content-Type: application/json

{
  "jobId": "job_7K92",
  "statusUrl": "/api/jobs/job_7K92",
  "resultUrl": "/api/reports/rpt_7K92"
}`,
      },
    ],
  },
  {
    group: 'Jobs (Async)',
    items: [
      {
        id: 'jobs-list',
        method: 'GET',
        path: '/api/jobs',
        title: 'List jobs',
        auth: 'Bearer',
        description:
          'Lista los jobs del tenant para monitorear renders asíncronos. Soporta filtro por estado para construir dashboards de estado.',
        requestParams: [
          { name: 'page', type: 'number', required: false, description: 'Página (default: 1).' },
          { name: 'limit', type: 'number', required: false, description: 'Elementos por página (default: 20).' },
          { name: 'status', type: 'queued | processing | completed | failed | canceled', required: false, description: 'Filtra por estado del job.' },
        ],
        responseParams: [
          { name: 'items', type: 'Job[]', required: true, description: 'Lista de jobs.' },
          { name: 'total', type: 'number', required: false, description: 'Total de jobs que coinciden con el filtro.' },
        ],
        requestExample: `GET /api/jobs?page=1&limit=20&status=processing
Authorization: Bearer <token>`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: application/json

{
  "items": [
    {
      "id": "job_7K92",
      "status": "processing",
      "report_id": null,
      "created_at": "2026-02-21T17:00:00.000Z",
      "updated_at": "2026-02-21T17:00:05.000Z"
    }
  ],
  "total": 1
}`,
      },
      {
        id: 'jobs-detail',
        method: 'GET',
        path: '/api/jobs/:jobId',
        title: 'Get job status',
        auth: 'Bearer',
        description:
          'Consulta el estado detallado de un job generado por /api/render/async. Haz polling cada 1-3 segundos hasta status=completed o failed.',
        requestParams: [
          { name: 'jobId', type: 'string (path)', required: true, description: 'ID del job a consultar.' },
        ],
        responseParams: [
          { name: 'id', type: 'string', required: true, description: 'ID del job.' },
          { name: 'status', type: 'queued | processing | completed | failed | canceled', required: true, description: 'Estado actual.' },
          { name: 'report_id', type: 'string', required: false, description: 'ID del reporte generado (disponible cuando completed).' },
          { name: 'error', type: 'string', required: false, description: 'Mensaje de error (disponible cuando failed).' },
        ],
        requestExample: `GET /api/jobs/job_7K92
Authorization: Bearer <token>`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: application/json

{
  "id": "job_7K92",
  "status": "completed",
  "report_id": "rpt_7K92",
  "created_at": "2026-02-21T17:00:00.000Z",
  "updated_at": "2026-02-21T17:00:08.000Z"
}`,
      },
      {
        id: 'jobs-cancel',
        method: 'POST',
        path: '/api/jobs/:jobId/cancel',
        title: 'Cancel job',
        auth: 'Bearer',
        description:
          'Cancela un job que aún se encuentre en estado queued o processing. Jobs ya completados o fallidos no pueden cancelarse.',
        requestParams: [
          { name: 'jobId', type: 'string (path)', required: true, description: 'ID del job a cancelar.' },
        ],
        responseParams: [
          { name: 'ok', type: 'boolean', required: true, description: 'true si la cancelación fue exitosa.' },
          { name: 'id', type: 'string', required: true, description: 'ID del job cancelado.' },
          { name: 'status', type: '"canceled"', required: true, description: 'Estado final del job.' },
        ],
        requestExample: `POST /api/jobs/job_7K92/cancel
Authorization: Bearer <token>`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: application/json

{
  "ok": true,
  "id": "job_7K92",
  "status": "canceled"
}`,
      },
    ],
  },
  {
    group: 'Reports',
    items: [
      {
        id: 'reports-download',
        method: 'GET',
        path: '/api/reports/:reportId',
        title: 'Download report',
        auth: 'Bearer',
        description:
          'Descarga el archivo final de un reporte cuando el job async ha terminado con status completed. El Content-Type varía según la receta utilizada.',
        requestParams: [
          { name: 'reportId', type: 'string (path)', required: true, description: 'ID del reporte a descargar.' },
        ],
        responseParams: [
          { name: '(body)', type: 'binary', required: true, description: 'Contenido del archivo generado.' },
          { name: 'Content-Type', type: 'header', required: true, description: 'application/pdf, image/png, text/html, etc.' },
        ],
        requestExample: `GET /api/reports/rpt_7K92
Authorization: Bearer <token>`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: application/pdf
Content-Disposition: attachment; filename="invoice-INV-001.pdf"

%PDF-1.7
...binary content...`,
      },
      {
        id: 'reports-list',
        method: 'GET',
        path: '/api/reports',
        title: 'List reports',
        auth: 'Bearer',
        description:
          'Lista los reportes generados en el tenant para historial y descarga posterior. Incluye metadatos pero no el contenido binario.',
        requestParams: [
          { name: 'page', type: 'number', required: false, description: 'Página (default: 1).' },
          { name: 'limit', type: 'number', required: false, description: 'Elementos por página (default: 20).' },
        ],
        responseParams: [
          { name: 'items', type: 'Report[]', required: true, description: 'Lista de reportes.' },
          { name: 'total', type: 'number', required: false, description: 'Total de reportes.' },
        ],
        requestExample: `GET /api/reports?page=1&limit=20
Authorization: Bearer <token>`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: application/json

{
  "items": [
    {
      "id": "rpt_7K92",
      "name": "invoice-INV-001.pdf",
      "recipe": "chrome-pdf",
      "size": 48320,
      "created_at": "2026-02-21T17:01:00.000Z"
    }
  ],
  "total": 1
}`,
      },
    ],
  },
  {
    group: 'Billing',
    items: [
      {
        id: 'billing-me',
        method: 'GET',
        path: '/api/billing/me',
        title: 'Current billing state',
        auth: 'Bearer',
        description:
          'Obtiene el plan activo, los límites, el uso acumulado del mes y el estado de suscripción del usuario actual. Útil para mostrar dashboards de consumo en tu UI.',
        requestParams: [
          { name: '—', type: '—', required: false, description: 'No requiere body ni parámetros de query.' },
        ],
        responseParams: [
          { name: 'plan', type: 'object | null', required: true, description: 'Detalles del plan activo.' },
          { name: 'limits', type: 'object', required: true, description: 'Límites del plan (renders/mes, calls/min, etc.).' },
          { name: 'usageMonthToDate', type: 'object', required: true, description: 'Uso acumulado desde inicio de mes.' },
          { name: 'features', type: 'object', required: true, description: 'Features habilitados en el plan.' },
          { name: 'status', type: 'string', required: true, description: 'Estado de la suscripción (active, past_due, canceled…).' },
        ],
        requestExample: `GET /api/billing/me
Authorization: Bearer <token>`,
        responseExample: `HTTP/1.1 200 OK
Content-Type: application/json

{
  "plan": {
    "code": "starter",
    "name": "Starter",
    "monthlyPrice": 299,
    "currency": "MXN"
  },
  "limits": {
    "rendersMonth": 1000,
    "apiCallsPerMinute": 60
  },
  "usageMonthToDate": {
    "rendersMonth": 123
  },
  "features": {
    "apiAccess": true,
    "customBranding": false,
    "webhooks": false
  },
  "status": "active"
}`,
      },
    ],
  },
];

const COMMON_ERRORS = [
  { code: '401', label: 'Unauthorized', msg: 'Token inválido, expirado o ausente.' },
  { code: '402', label: 'Payment Required', msg: 'Suscripción suspendida, pendiente de pago o past_due.' },
  { code: '403', label: 'Forbidden', msg: 'Feature no incluida en el plan activo o falta de permisos de rol.' },
  { code: '404', label: 'Not Found', msg: 'Recurso no encontrado en el tenant.' },
  { code: '422', label: 'Validation Error', msg: 'Payload inválido. El body de error incluye detalles por campo.' },
  { code: '429', label: 'Too Many Requests', msg: 'Límite de rate del plan excedido. Reintentar con backoff.' },
  { code: '500', label: 'Internal Error', msg: 'Error interno del servidor. Incluye un X-Request-Id para soporte.' },
];

function methodClass(method) {
  switch (String(method).toUpperCase()) {
    case 'GET': return 'is-get';
    case 'POST': return 'is-post';
    case 'PATCH': return 'is-patch';
    case 'DELETE': return 'is-delete';
    case 'PUT': return 'is-put';
    default: return '';
  }
}

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  const copy = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 2000);
    });
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return [copied, copy];
}

function CopyBtn({ text, label = 'Copiar' }) {
  const [copied, copy] = useCopyToClipboard();

  return (
    <button
      type="button"
      className={`api-ref-copy-btn${copied ? ' copied' : ''}`}
      onClick={() => copy(text)}
      title={copied ? 'Copiado' : label}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {copied ? 'Copiado' : label}
    </button>
  );
}

function CodeBlock({ lang, code }) {
  return (
    <div className="api-ref-code-block">
      <div className="api-ref-code-block__header">
        <span className="api-ref-code-block__lang">{lang}</span>
        <CopyBtn text={code} />
      </div>
      <pre className="api-ref-code-block__body">{code}</pre>
    </div>
  );
}

function AuthBadge({ auth }) {
  if (auth === 'Bearer') {
    return (
      <span className="api-ref-auth-badge is-bearer">
        <Lock size={12} />
        Bearer JWT
      </span>
    );
  }

  return (
    <span className="api-ref-auth-badge is-none">
      <Unlock size={12} />
      Sin autenticación
    </span>
  );
}

function ParamTable({ params }) {
  return (
    <table className="api-ref-param-table">
      <thead>
        <tr>
          <th>Nombre</th>
          <th>Tipo</th>
          <th>Descripción</th>
        </tr>
      </thead>
      <tbody>
        {params.map((p) => (
          <tr key={p.name}>
            <td>
              {p.name}
              {p.required && p.name !== '—'
                ? <span className="api-ref-param-required">requerido</span>
                : p.name !== '—' && <span className="api-ref-param-optional">opcional</span>}
            </td>
            <td>
              {p.type !== '—' && <span className="api-ref-type">{p.type}</span>}
            </td>
            <td>{p.description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SpecPane({ item }) {
  const [pathCopied, copyPath] = useCopyToClipboard();

  return (
    <div className="api-ref-spec">
      <div className="api-ref-spec__endpoint">
        <span className={`api-method ${methodClass(item.method)}`}>{item.method}</span>
        <code className="api-ref-spec__path">{item.path}</code>
        <button
          type="button"
          className="api-ref-spec__copy-path"
          onClick={() => copyPath(item.path)}
        >
          {pathCopied ? <Check size={11} /> : <Copy size={11} />}
          {pathCopied ? 'Copiado' : 'Copiar ruta'}
        </button>
      </div>

      <h1 className="api-ref-spec__title">{item.title}</h1>
      <p className="api-ref-spec__desc">{item.description}</p>

      <div className="api-ref-section">
        <h2 className="api-ref-section__title">Autenticación</h2>
        <AuthBadge auth={item.auth} />
      </div>

      <div className="api-ref-section">
        <h2 className="api-ref-section__title">Parámetros de request</h2>
        <ParamTable params={item.requestParams} />
      </div>

      <div className="api-ref-section">
        <h2 className="api-ref-section__title">Campos de respuesta</h2>
        <ParamTable params={item.responseParams} />
      </div>

      <div className="api-ref-section">
        <h2 className="api-ref-section__title">Errores frecuentes</h2>
        <div className="api-ref-errors-list">
          {COMMON_ERRORS.map((e) => (
            <div key={e.code} className="api-ref-error-row">
              <span className={`api-ref-error-code ${Number(e.code) >= 500 ? 'is-5xx' : 'is-4xx'}`}>
                {e.code}
              </span>
              <span className="api-ref-error-msg">
                <strong>{e.label}:</strong> {e.msg}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="api-ref-examples-mobile">
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid rgba(51,65,85,0.4)' }}>
          <span style={{ fontSize: '0.76rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Ejemplos
          </span>
        </div>
        <div style={{ padding: '1.25rem' }}>
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
              Request example
            </div>
            <CodeBlock lang="http" code={item.requestExample} />
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>
              Response example
            </div>
            <CodeBlock lang="http" code={item.responseExample} />
          </div>
        </div>
      </div>
    </div>
  );
}

function ExamplesPane({ item }) {
  return (
    <aside className="api-ref-examples">
      <div style={{ padding: '1.5rem 1.5rem 0' }}>
        <div className="api-ref-intro">
          <div className="api-ref-intro__badge">
            <span className="api-ref-status-dot" />
            API v2
          </div>
          <h2>ReportEngine API</h2>
          <p>REST API con autenticación JWT. Todas las rutas devuelven JSON salvo los endpoints de render que devuelven el binario.</p>
          <div className="api-ref-intro__pills">
            <div className="api-ref-intro__pill"><span>JSON</span> Application/JSON</div>
            <div className="api-ref-intro__pill"><span>JWT</span> Bearer token</div>
            <div className="api-ref-intro__pill"><span>HTTPS</span> TLS 1.2+</div>
          </div>
        </div>
      </div>

      <div className="api-ref-examples__block">
        <div className="api-ref-examples__label">
          <span>Request example</span>
          <span style={{ color: '#334155', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.7rem' }}>
            {item.method} {item.path}
          </span>
        </div>
        <CodeBlock lang="http" code={item.requestExample} />
      </div>

      <div className="api-ref-examples__block">
        <div className="api-ref-examples__label">
          <span>Response example</span>
        </div>
        <CodeBlock lang="json" code={item.responseExample} />
      </div>
    </aside>
  );
}

export default function ApiReferencePage() {
  const flat = useMemo(() => API_SECTIONS.flatMap((s) => s.items), []);
  const [activeId, setActiveId] = useState(flat[0]?.id ?? '');
  const activeItem = useMemo(() => flat.find((i) => i.id === activeId) ?? flat[0], [flat, activeId]);

  const apiBaseUrl = useMemo(() => {
    const envUrl = import.meta.env?.VITE_API_URL?.trim?.() ?? '';
    if (envUrl) {
      const cleaned = envUrl.replace(/\/$/, '');
      return cleaned.endsWith('/api') ? cleaned : `${cleaned}/api`;
    }
    if (typeof window !== 'undefined') return `${window.location.origin}/api`;
    return 'https://tu-dominio.com/api';
  }, []);

  return (
    <div className="api-ref-page">
      <header className="api-ref-topbar">
        <div className="api-ref-topbar__inner">
          <Link to="/" className="api-ref-topbar__brand">
            <div className="api-ref-topbar__logo">
              <FileText size={14} color="white" />
            </div>
            <span className="api-ref-topbar__product">ReportEngine</span>
            <span className="api-ref-topbar__sep">/</span>
            <span className="api-ref-topbar__section">API Reference</span>
          </Link>

          <div className="api-ref-topbar__base-url">
            <Terminal size={12} style={{ color: '#475569' }} />
            Base URL:
            <code>{apiBaseUrl}</code>
          </div>

          <nav className="api-ref-topbar__nav">
            <Link to="/documentacion" className="api-ref-topbar__link">
              <Hash size={12} />
              Help Center
            </Link>
            <a href="#" className="api-ref-topbar__link is-active" style={{ color: '#22D3EE' }}>
              <Code2 size={12} />
              API Reference
            </a>
            <Link to="/estado-del-sistema" className="api-ref-topbar__link">
              <Activity size={12} />
              Estado
            </Link>
            <Link
              to="/dashboard"
              className="api-ref-topbar__link"
              style={{ padding: '5px 12px', background: 'rgba(79,70,229,0.12)', border: '1px solid rgba(79,70,229,0.3)', borderRadius: 8, color: '#a5b4fc' }}
            >
              Dashboard
              <ExternalLink size={11} />
            </Link>
          </nav>
        </div>
      </header>

      <div className="api-ref-body">
        <nav className="api-ref-sidebar">
          <div className="api-ref-sidebar__head">
            <p>Selecciona un endpoint para ver su documentación completa.</p>
          </div>

          {API_SECTIONS.map((section, sIdx) => (
            <div key={section.group} className="api-ref-nav-group">
              {sIdx > 0 && <div className="api-ref-nav-divider" />}
              <div className="api-ref-nav-group__label">{section.group}</div>
              {section.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`api-ref-nav-item${activeItem?.id === item.id ? ' is-active' : ''}`}
                  onClick={() => setActiveId(item.id)}
                >
                  <span className={`api-method ${methodClass(item.method)}`}>{item.method}</span>
                  <span className="api-ref-nav-item__title">{item.title}</span>
                </button>
              ))}
            </div>
          ))}

          <div style={{ padding: '2rem 1.25rem 0', borderTop: '1px solid rgba(51,65,85,0.4)', marginTop: '1.5rem' }}>
            <div className="api-ref-nav-group__label" style={{ paddingLeft: 0 }}>Recursos</div>
            {[
              { label: 'Changelog', icon: <ChevronRight size={12} /> },
              { label: 'SDKs & Librerías', icon: <ChevronRight size={12} /> },
              { label: 'Postman Collection', icon: <ExternalLink size={11} /> },
            ].map((l) => (
              <a
                key={l.label}
                href="#"
                className="api-ref-nav-item"
                style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', gap: '0.625rem' }}
              >
                <span className="api-ref-nav-item__title">{l.label}</span>
                <span style={{ color: '#475569', marginLeft: 'auto' }}>{l.icon}</span>
              </a>
            ))}
          </div>
        </nav>

        {activeItem && <SpecPane item={activeItem} />}
        {activeItem && <ExamplesPane item={activeItem} />}
      </div>
    </div>
  );
}
